import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { interval, Subject, Subscription, timer } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { isDefined } from '../utils';
import { FiGradientTimerService, ITimerGradientColor, TimerGradientColorType } from './fi-gradient-timer.service';

@Component({
    selector: 'fi-gradient-timer',
    templateUrl: './fi-gradient-timer.component.html',
    styleUrls: ['./fi-gradient-timer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-gradient-timer' },
    standalone: false,
})
export class FiGradientTimerComponent implements OnInit, OnDestroy {
    public countdownDurationMS = input<number>(); // use only when countdown duration is known
    public dueDateTime = input<string>(); // use only when countdown duration is not known
    public dueDateTimeZone = input<string>(); // use only when countdown duration is not known
    public reminderCountdownTimespanMS = input<number>(900000); // default is 15 min 900000, use only when countdown duration is not known
    public size = input<number>(175); // Size in pixels (width and height)
    public strokeWidth = input<number>(18);
    public isTimerIconShown = input<boolean>(true);
    public fillDirection = input<'clockwise' | 'anticlockwise'>('clockwise');
    public normalTimerProgressValuePercent = input<number>(50);
    public warningTimerProgressValuePercent = input<number>(75);
    public displayAfterTimeIsOut = input<boolean>(false);
    public isInitiallyFilled = input<boolean>(false);
    public remainingTimeDelayMS = input<number>(5000); // 5 seconds delay to ensure the timer is displayed correctly in checklist module

    public timeIsOut = output<void>();

    private fiGradientTimerService = inject(FiGradientTimerService);

    private remainingTimeMS = signal<number>(0);
    private timerSubscription: Subscription | undefined;
    private countdownStartTime = signal<number | undefined>(undefined);
    private timerCompleted = signal<boolean>(false);

    private isSimpleMode = computed(() => isDefined(this.countdownDurationMS()));

    public remainingTimeValue = computed(() => {
        const remainingTimeMs = this.remainingTimeMS();
        return this.fiGradientTimerService.formatRemainingTime(remainingTimeMs, this.displayAfterTimeIsOut());
    });

    private effectiveDurationMS = computed(() => {
        const countdownDuration = this.countdownDurationMS();
        if (isDefined(countdownDuration)) {
            return countdownDuration;
        }
        return this.reminderCountdownTimespanMS();
    });

    protected timerProgressValuePercent = computed(() => {
        const remainingTimeMS = this.remainingTimeMS();
        const durationMS = this.effectiveDurationMS();
        const isInitiallyFilled = this.isInitiallyFilled();
        if (remainingTimeMS <= 0 || durationMS <= 0) {
            return isInitiallyFilled ? 0 : 100;
        }
        if (isInitiallyFilled) {
            return Math.round((remainingTimeMS * 100) / durationMS);
        }
        return 100 - Math.round((remainingTimeMS * 100) / durationMS);
    });

    private currentColorType = computed(() => {
        if (this.fillDirection() === 'anticlockwise') {
            this.currentColorTypeAnticlockwise();
        }

        return this.currentColorTypeClockwise();
    });

    private currentColorTypeAnticlockwise = computed(() => {
        const timerProgressValuePercent = this.timerProgressValuePercent();
        if (timerProgressValuePercent >= this.normalTimerProgressValuePercent()) {
            return TimerGradientColorType.Normal;
        }
        if (timerProgressValuePercent >= this.warningTimerProgressValuePercent()) {
            return TimerGradientColorType.Warning;
        }
        return TimerGradientColorType.RunningOut;
    });

    private currentColorTypeClockwise = computed(() => {
        const timerProgressValuePercent = this.timerProgressValuePercent();

        if (timerProgressValuePercent <= this.normalTimerProgressValuePercent()) {
            return TimerGradientColorType.Normal;
        }

        if (timerProgressValuePercent <= this.warningTimerProgressValuePercent()) {
            return TimerGradientColorType.Warning;
        }

        return TimerGradientColorType.RunningOut;
    });

    protected timerGradientColorsComputed = computed(() => {
        if (!this.remainingTimeValue()) {
            return undefined;
        }

        const colorType = this.currentColorType();
        return this.fiGradientTimerService.timerGradientColors.find(
            (timerGradientColor: ITimerGradientColor) => timerGradientColor.type === colorType,
        );
    });

    public ngOnInit(): void {
        if (this.isSimpleMode()) {
            this.startSimpleCountdownTimer();
            return;
        }

        this.scheduleCountdownTimerStart();
    }

    private startSimpleCountdownTimer(): void {
        const currentCountdownDuration = this.countdownDurationMS();
        if (!isDefined(currentCountdownDuration)) {
            return;
        }
        this.remainingTimeMS.set(currentCountdownDuration);
        this.startCountdownTimer();
    }

    private scheduleCountdownTimerStart(): void {
        const dueDateTime = this.dueDateTime();
        const dueDateTimeZone = this.dueDateTimeZone();
        if (!isDefined(dueDateTime) || !isDefined(dueDateTimeZone)) {
            return;
        }

        const totalRemainingTimeMS = this.getTotalRemainingTimeMS();
        if (totalRemainingTimeMS <= 0) {
            return;
        }

        const reminderCountdownTimespanMS = this.reminderCountdownTimespanMS();
        if (totalRemainingTimeMS <= reminderCountdownTimespanMS) {
            this.remainingTimeMS.set(totalRemainingTimeMS);
            this.startCountdownTimer();
        } else {
            this.scheduleTimerStart(totalRemainingTimeMS - reminderCountdownTimespanMS);
        }
    }

    private getTotalRemainingTimeMS(): number {
        return this.fiGradientTimerService.getTotalRemainingTimeMS(
            this.dueDateTime(),
            this.dueDateTimeZone(),
            this.remainingTimeDelayMS(),
        );
    }

    private getRemainingTimeMS(): number {
        if (this.isSimpleMode()) {
            return this.remainingTimeMS();
        }
        return this.getTotalRemainingTimeMS();
    }

    private scheduleTimerStart(delayMS: number): void {
        if (delayMS <= 0) {
            this.startCountdownTimer();
            return;
        }

        const maxAvailableDelay = this.fiGradientTimerService.maxAvailableDelay;

        const delayObservable =
            delayMS > maxAvailableDelay
                ? timer(maxAvailableDelay).pipe(switchMap(() => timer(delayMS - maxAvailableDelay)))
                : timer(delayMS);

        this.timerSubscription = delayObservable.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            const totalRemainingTimeMS = this.getTotalRemainingTimeMS();
            if (totalRemainingTimeMS > 0 && totalRemainingTimeMS <= this.reminderCountdownTimespanMS()) {
                this.remainingTimeMS.set(totalRemainingTimeMS);
                this.startCountdownTimer();
            }
        });
    }

    private startCountdownTimer(): void {
        this.cleanupSubscription();

        if (this.getRemainingTimeMS() <= 0) {
            return;
        }

        if (this.isSimpleMode()) {
            this.countdownStartTime.set(Date.now());
        }

        this.timerSubscription = timer(1000, 1000)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                if (this.isSimpleMode()) {
                    const current = this.remainingTimeMS();
                    const next = Math.max(0, current - 1000);
                    if (next <= 0) {
                        this.stopTimer(true);
                        return;
                    }
                    this.remainingTimeMS.set(next);
                } else {
                    const remainingTimeMS = this.getRemainingTimeMS();
                    if (remainingTimeMS <= 0) {
                        this.stopTimer(true);
                        return;
                    }
                    this.remainingTimeMS.set(remainingTimeMS);
                }
            });
    }

    private cleanupSubscription(): void {
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
            this.timerSubscription = undefined;
        }
    }

    private stopTimer(shouldEmitTimeIsOut: boolean = false, resetCompleted: boolean = true): void {
        this.cleanupSubscription();
        this.remainingTimeMS.set(0);
        this.countdownStartTime.set(undefined);
        if (shouldEmitTimeIsOut) {
            this.timerCompleted.set(true);
            this.timeIsOut.emit();
        } else if (resetCompleted) {
            this.timerCompleted.set(false);
        }
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.stopTimer();
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
